import React, { CSSProperties } from "react";
import {
  useTranslate,
  useLogout,
  useTitle,
  useIsExistAuthentication,
  useRouterContext,
  useRefineContext,
  useLink,
  useRouterType,
  useActiveAuthProvider,
  useWarnAboutChange,
} from "@refinedev/core";
import { useThemedLayoutContext } from "@refinedev/antd";
import {
  DashboardOutlined,
  LogoutOutlined,
  BarsOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Layout, Menu, Grid, Drawer, Button, theme } from "antd";
import type { RefineThemedLayoutV2SiderProps } from "@refinedev/antd";
import { ThemedTitleV2 } from "./title";
import { useLocation } from "react-router-dom";
import {
  useMenuTree,
  findSelectedKey,
  findOpenKeys,
  type MenuNode,
} from "./menuItems";

const drawerButtonStyles: CSSProperties = {
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  position: "fixed",
  top: 64,
  zIndex: 999,
};

export const ThemedSiderV2: React.FC<RefineThemedLayoutV2SiderProps> = ({
  Title: TitleFromProps,
  render,
  fixed,
}) => {
  const { token } = theme.useToken();
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();

  // Load initial collapsed state from localStorage
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const savedState = localStorage.getItem("refine-sider-collapsed");
      if (savedState !== null) {
        console.log("here 3", savedState);
        setSiderCollapsed(JSON.parse(savedState));
      }
    }
  }, []);

  const isExistAuthentication = useIsExistAuthentication();
  const routerType = useRouterType();
  const NewLink = useLink();
  const { warnWhen, setWarnWhen } = useWarnAboutChange();
  const { Link: LegacyLink } = useRouterContext();
  const Link = routerType === "legacy" ? LegacyLink : NewLink;
  const TitleFromContext = useTitle();
  const translate = useTranslate();
  const location = useLocation();
  const menuTree = useMenuTree();
  const selectedKey = findSelectedKey(menuTree, location.pathname);
  const defaultOpenKeys = findOpenKeys(menuTree, selectedKey);
  const breakpoint = Grid.useBreakpoint();
  const { hasDashboard } = useRefineContext();
  const authProvider = useActiveAuthProvider();
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });

  const isMobile =
    typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  const RenderToTitle = TitleFromProps ?? TitleFromContext ?? ThemedTitleV2;

  const handleLogout = () => {
    if (warnWhen) {
      const confirm = window.confirm(
        translate(
          "warnWhenUnsavedChanges",
          "Are you sure you want to leave? You have unsaved changes.",
        ),
      );

      if (confirm) {
        setWarnWhen(false);
        mutateLogout();
      }
    } else {
      mutateLogout();
    }
  };

  const logout = isExistAuthentication && (
    <Menu.Item
      key="logout"
      onClick={() => handleLogout()}
      icon={<LogoutOutlined style={{ fontSize: 18 }} />}
    >
      {translate("buttons.logout", "Logout")}
    </Menu.Item>
  );

  const dashboard = hasDashboard ? (
    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
      <Link to="/">{translate("dashboard.title", "Dashboard")}</Link>
      {!siderCollapsed && selectedKey === "/" && (
        <div className="ant-menu-tree-arrow" />
      )}
    </Menu.Item>
  ) : null;

  const renderMenuItems = (nodes: MenuNode[]): JSX.Element[] => {
    return nodes.map((item) => {
      const { icon, label, route, key, children } = item;

      if (children.length > 0) {
        return (
          <Menu.SubMenu key={key} icon={icon} title={label}>
            {renderMenuItems(children)}
          </Menu.SubMenu>
        );
      }

      const isSelected = key === selectedKey;

      return (
        <Menu.Item
          key={key}
          icon={icon}
          title={siderCollapsed ? label : undefined}
        >
          <Link to={route ?? ""}>{label}</Link>
          {!siderCollapsed && isSelected && (
            <div className="ant-menu-tree-arrow" />
          )}
        </Menu.Item>
      );
    });
  };

  const items = renderMenuItems(menuTree);

  const renderSider = () => {
    if (render) {
      return render({
        dashboard,
        items,
        logout,
        collapsed: siderCollapsed,
      });
    }
    return (
      <>
        {dashboard}
        <div className="mt-4"></div>
        {items}
        <div className="hidden">{logout}</div>
      </>
    );
  };

  const renderMenu = () => {
    return (
      <Menu
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={defaultOpenKeys}
        mode="inline"
        style={{
          paddingTop: "20px",
          paddingLeft: "16px",
          paddingRight: "16px",
          border: "none",
          overflow: "auto",
          height: "calc(100% - 72px)",
          width: siderCollapsed ? "80px" : "250px",
          backgroundColor: token.colorBgElevated,
        }}
        onClick={() => {
          setMobileSiderOpen(false);
        }}
      >
        {renderSider()}
      </Menu>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <Drawer
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          placement="left"
          closable={false}
          width={250}
          bodyStyle={{
            padding: 0,
          }}
          maskClosable={true}
        >
          <Layout>
            <Layout.Sider
              style={{
                height: "100vh",
                backgroundColor: token.colorBgElevated,
                borderRight: `1px solid ${token.colorBgElevated}`,
              }}
            >
              <div
                style={{
                  width: "250px",
                  padding: "0 16px",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  height: "64px",
                  backgroundColor: token.colorBgElevated,
                }}
              >
                <RenderToTitle collapsed={false} />
              </div>
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
        <Button
          style={drawerButtonStyles}
          size="large"
          onClick={() => setMobileSiderOpen(true)}
          icon={<BarsOutlined />}
        />
      </>
    );
  };

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRight: `1px solid ${token.colorBgElevated}`,
    width: "250px",
  };

  if (fixed) {
    siderStyles.position = "fixed";
    siderStyles.top = 0;
    siderStyles.height = "100vh";
    siderStyles.zIndex = 999;
  }

  return (
    <>
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? "80px" : "250px",
            transition: "all 0.2s",
          }}
        />
      )}
      <Layout.Sider
        style={siderStyles}
        width={250}
        className="shadow-sm"
        collapsible
        collapsed={siderCollapsed}
        onCollapse={(collapsed, type) => {
          if (type === "clickTrigger") {
            setSiderCollapsed(collapsed);
            console.log("here 2");
            localStorage.setItem(
              "refine-sider-collapsed",
              JSON.stringify(collapsed),
            );
          }
        }}
        collapsedWidth={80}
        breakpoint="lg"
        trigger={
          <Button
            type="text"
            style={{
              borderRadius: 0,
              height: "100%",
              width: "100%",
              backgroundColor: token.colorBgElevated,
            }}
          >
            {siderCollapsed ? (
              <RightOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            ) : (
              <LeftOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            )}
          </Button>
        }
      >
        <div
          style={{
            width: siderCollapsed ? "80px" : "250px",
            padding: siderCollapsed ? "0" : "0 16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "110px",
            backgroundColor: token.colorBgElevated,
            fontSize: "14px",
            marginBottom: "0px",
          }}
        >
          <RenderToTitle collapsed={siderCollapsed} />
        </div>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};
